import dayjs from 'dayjs';
import { formatLocalDate } from '../dateUtils';
import { FULL_DAY_NAMES, DAY_CODE_TO_INDEX_MAP } from '../constants/dates';

/**
 * Formats a date into a readable format
 * @param {Date|string|number} date - The date to format (Date object, ISO string, or Unix timestamp)
 * @param {boolean} [abbreviated=false] - Whether to use abbreviated month format
 * @returns {string} Formatted date string (e.g. "April 8" or "Apr 8")
 */
export const formatDate = (date, abbreviated = false) => {
  if (!date) return '';
  const month = formatLocalDate(date, { month: abbreviated ? 'short' : 'long' });
  return `${month[0].toUpperCase() + month.slice(1)} ${dayjs(date).format('D')}`;
};

/**
 * Formats a weekday name consistently across the app
 * @param {Date|string|number} date - The date to format
 * @param {boolean} [short=true] - Whether to use short format (e.g. "Mon" vs "Monday")
 * @returns {string} Formatted weekday name
 */
export const formatWeekdayName = (date, short = true) => {
  const dayOfWeek = formatLocalDate(date, { weekday: short ? 'short' : 'long' });
  return dayOfWeek;
};

/**
 * Formats an array of recurring days into a readable string
 * @param {Array<string|number>} days - Array of day codes or indices
 * @returns {string} Formatted string of recurring days (e.g. "every Monday, Wednesday, Friday")
 */
export const formatRecurringDays = (days) => {
  if (!days?.length) return '';

  const dayIndexes = days.map((day) => (typeof day === 'string' ? DAY_CODE_TO_INDEX_MAP[day] : day));

  return dayIndexes.length === 1
    ? `every ${FULL_DAY_NAMES[dayIndexes[0]]}`
    : `every ${dayIndexes.map((i) => FULL_DAY_NAMES[i]).join(', ')}`;
};
